/**
 * Pure helpers for the staff form's Menu tab: turning salon services (and the
 * staff member's existing `servicing_staff` rows) into editable assignments,
 * and diffing the edited assignments back into POST/PUT/DELETE calls.
 */
import {
  DAY_ALLOWED_KEYS,
  extractDayFlags,
} from "../ServiceFormWizard/dayAvailability";
import type { StaffServiceSettingsData } from "../StaffServiceSettings/StaffServiceSettings";
import type { SalonService, StaffService } from "../../../types/salon";
import type {
  StaffServiceAssignment,
  StaffServiceAssignments,
  StaffServiceFields,
  StaffServicesDiff,
} from "../../../types/staffForm";

/** Legacy payloads still carry `default_duration_mins` / `duration_mins`. */
type ServiceRecord = SalonService & { default_duration_mins?: number };
type StaffServiceRecord = StaffService & { duration_mins?: number };

const toMinutes = (value: string | number | null | undefined): number =>
  Number.parseInt(String(value ?? "0"), 10) || 0;

/** "1 hr 15 min" / "45 min" — matches the duration Select labels. */
export function formatDurationMinutes(minutes: string | number): string {
  const total = toMinutes(minutes);
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours === 0) return `${mins} min`;
  const hourLabel = `${hours} hr${hours > 1 ? "s" : ""}`;
  return mins > 0 ? `${hourLabel} ${mins} min` : hourLabel;
}

/** A not-yet-assigned service, seeded from the service's defaults. */
export function assignmentFromService(
  service: ServiceRecord,
): StaffServiceAssignment {
  return {
    id: null,
    enabled: false,
    isOpen: false,
    price: service.default_price ?? 0,
    duration: String(
      service.default_base_duration_mins || service.default_duration_mins || 60,
    ),
    processingTime: String(service.default_processing_time_mins ?? 0),
    finishingTime: String(service.default_finishing_time_mins ?? 0),
    bufferTime: String(service.default_buffer_time_mins ?? 0),
    startsAt: !!service.default_is_variable_price,
    is_self_servicable: service.default_is_self_servicable !== false,
    ...extractDayFlags(service),
  };
}

/** An existing staff_services row; missing columns fall back to the service defaults. */
export function assignmentFromStaffService(
  staffService: StaffServiceRecord,
  service: ServiceRecord,
): StaffServiceAssignment {
  const defaults = assignmentFromService(service);
  const dayFlags = DAY_ALLOWED_KEYS.reduce(
    (flags, key) => ({ ...flags, [key]: staffService[key] ?? defaults[key] }),
    {} as Pick<StaffServiceAssignment, (typeof DAY_ALLOWED_KEYS)[number]>,
  );
  return {
    ...defaults,
    ...dayFlags,
    id: staffService.id,
    enabled: true,
    price: staffService.price ?? defaults.price,
    duration: String(
      staffService.base_duration_mins ??
        staffService.duration_mins ??
        defaults.duration,
    ),
    processingTime: String(
      staffService.processing_time_mins ?? defaults.processingTime,
    ),
    finishingTime: String(
      staffService.finishing_time_mins ?? defaults.finishingTime,
    ),
    bufferTime: String(staffService.buffer_time_mins ?? defaults.bufferTime),
    startsAt: staffService.is_variable_price ?? defaults.startsAt,
    is_self_servicable:
      staffService.is_self_servicable ?? defaults.is_self_servicable,
  };
}

/**
 * One assignment per salon service: the staff member's existing row where
 * there is one (read from `services[].servicing_staff`), else the service's
 * defaults, disabled. `staffId` is undefined for a new staff member.
 */
export function buildAssignmentsForStaff(
  services: ServiceRecord[] | null | undefined,
  staffId?: string | null,
): StaffServiceAssignments {
  return (services ?? []).reduce((acc, service) => {
    const existing = staffId
      ? service.servicing_staff?.find((row) => row.staff_id === staffId)
      : undefined;
    acc[service.id] = existing
      ? assignmentFromStaffService(existing, service)
      : assignmentFromService(service);
    return acc;
  }, {} as StaffServiceAssignments);
}

/**
 * Fold an edit from `StaffServiceSettingsFields` (which works on the narrower
 * StaffServiceSettingsData) back into the full assignment, keeping the
 * day flags and filling the optional durations it may leave undefined.
 */
export function mergeServiceSettings(
  assignment: StaffServiceAssignment,
  settings: StaffServiceSettingsData,
): StaffServiceAssignment {
  return {
    ...assignment,
    ...settings,
    id: assignment.id,
    processingTime: settings.processingTime ?? assignment.processingTime,
    finishingTime: settings.finishingTime ?? assignment.finishingTime,
    bufferTime: settings.bufferTime ?? assignment.bufferTime,
  };
}

/** Only the rows that exist server-side (the diff baseline). */
export function persistedAssignments(
  assignments: StaffServiceAssignments,
): StaffServiceAssignments {
  return Object.fromEntries(
    Object.entries(assignments).filter(([, a]) => a.enabled && !!a.id),
  );
}

/**
 * The staff_services columns for an assignment — a strict whitelist, because
 * POST/PUT /services reject any unknown key (e.g. the UI-only `enabled` /
 * `isOpen`) with a 400.
 */
export function staffServiceFieldsFromAssignment(
  assignment: StaffServiceAssignment,
): StaffServiceFields {
  return {
    price: Number(assignment.price) || 0,
    base_duration_mins: toMinutes(assignment.duration),
    processing_time_mins: toMinutes(assignment.processingTime),
    finishing_time_mins: toMinutes(assignment.finishingTime),
    buffer_time_mins: toMinutes(assignment.bufferTime),
    is_variable_price: !!assignment.startsAt,
    is_self_servicable: !!assignment.is_self_servicable,
    ...extractDayFlags(assignment),
  };
}

const sameFields = (a: StaffServiceFields, b: StaffServiceFields): boolean =>
  (Object.keys(a) as (keyof StaffServiceFields)[]).every(
    (key) => a[key] === b[key],
  );

/**
 * What to send so the server matches the edited Menu:
 * - enabled + persisted + changed → update (unchanged rows are not re-sent)
 * - enabled + not persisted       → create
 * - disabled + persisted          → delete
 * Rows absent from `desired` (e.g. a service not rendered) are left alone, as
 * are rows in `skipCreate` — creates the server accepted but whose new id it
 * didn't return, so re-sending them would add a duplicate row.
 */
export function diffStaffServices({
  baseline,
  desired,
  staffId,
  skipCreate,
}: {
  baseline: StaffServiceAssignments;
  desired: StaffServiceAssignments;
  staffId: string;
  skipCreate?: ReadonlySet<string>;
}): StaffServicesDiff {
  const diff: StaffServicesDiff = { toCreate: [], toUpdate: [], toDelete: [] };
  Object.entries(desired).forEach(([serviceId, wanted]) => {
    const persisted = baseline[serviceId];
    const persistedId = persisted?.enabled ? persisted.id : null;
    const fields = staffServiceFieldsFromAssignment(wanted);
    if (wanted.enabled && persistedId) {
      if (!sameFields(fields, staffServiceFieldsFromAssignment(persisted))) {
        diff.toUpdate.push({ id: persistedId, ...fields });
      }
    } else if (wanted.enabled && !skipCreate?.has(serviceId)) {
      diff.toCreate.push({
        salon_service_id: serviceId,
        staff_id: staffId,
        ...fields,
      });
    } else if (persistedId) {
      diff.toDelete.push(persistedId);
    }
  });
  return diff;
}

/**
 * `POST /services` returns the created salon services followed by the created
 * staff_services rows (`id` = staff_services uuid, `salon_service_id` = the
 * parent's uuid). Map salon_service_id → new staff_services id.
 */
export function createdStaffServiceIds(
  response: unknown,
): Record<string, string> {
  const rows = Array.isArray(response) ? response : [];
  return rows.reduce(
    (acc, row) => {
      if (row?.salon_service_id && row?.id) {
        acc[row.salon_service_id] = row.id;
      }
      return acc;
    },
    {} as Record<string, string>,
  );
}

export interface ServiceGroup {
  type: string;
  services: SalonService[];
}

/** Services grouped by `service_type` (first-seen order); untyped → "Other". */
export function groupServicesByType(
  services: SalonService[] | null | undefined,
): ServiceGroup[] {
  const groups = new Map<string, SalonService[]>();
  (services ?? []).forEach((service) => {
    const type = service.service_type?.trim() || "Other";
    groups.set(type, [...(groups.get(type) ?? []), service]);
  });
  return Array.from(groups, ([type, grouped]) => ({
    type,
    services: grouped,
  }));
}
